import { z } from 'zod';
import {
  CONTACT_LOCATIONS, FIELD_TYPES, GENDERS, MARITAL_STATUSES, MEMBERSHIPS, PERSON_STATUSES,
} from '../enums';
import { normalizePhone } from '../phone';
import { zEmail, zId, zOptDate, zOptId, zOptText, zText } from './common';

export const emailInputSchema = z.object({
  address: zEmail,
  location: z.enum(CONTACT_LOCATIONS).default('home'),
  isPrimary: z.boolean().default(false),
});

export const phoneInputSchema = z.object({
  number: z.string().trim().min(1).max(40).transform((v, ctx) => {
    const e164 = normalizePhone(v);
    if (!e164) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Teléfono inválido' });
      return z.NEVER;
    }
    return e164;
  }),
  location: z.enum(CONTACT_LOCATIONS).default('mobile'),
  isPrimary: z.boolean().default(false),
  canReceiveSms: z.boolean().default(true),
});

export const addressInputSchema = z.object({
  street: zText(200),
  street2: zOptText(200),
  city: zText(80),
  state: zOptText(40),
  zip: zOptText(20),
  country: z.string().trim().length(2).toUpperCase().default('US'),
  location: z.enum(CONTACT_LOCATIONS).default('home'),
  isPrimary: z.boolean().default(false),
});

const personBase = z.object({
  firstName: zText(80),
  lastName: zText(80),
  middleName: zOptText(80),
  nickname: zOptText(80),
  namePrefix: zOptText(20),
  nameSuffix: zOptText(20),
  birthdate: zOptDate,
  anniversary: zOptDate,
  gender: z.enum(GENDERS).nullable().optional(),
  isChild: z.boolean().optional(),
  grade: z.number().int().min(-1).max(12).nullable().optional(),
  graduationYear: z.number().int().min(1900).max(2200).nullable().optional(),
  school: zOptText(120),
  membership: z.enum(MEMBERSHIPS).optional(),
  status: z.enum(PERSON_STATUSES).optional(),
  inactiveReason: zOptText(200),
  maritalStatus: z.enum(MARITAL_STATUSES).nullable().optional(),
  /** Idioma de correos y avisos; null = el de la iglesia. */
  language: z.enum(['es', 'en']).nullable().optional(),
  medicalNotes: zOptText(2000),
  primaryCampusId: zOptId,
});

export const personCreateSchema = personBase.extend({
  emails: z.array(emailInputSchema).max(10).default([]),
  phones: z.array(phoneInputSchema).max(10).default([]),
  addresses: z.array(addressInputSchema).max(5).default([]),
  householdId: zOptId,
});
export const personUpdateSchema = personBase.partial();

export const contactsReplaceSchema = z.object({
  emails: z.array(emailInputSchema).max(10),
  phones: z.array(phoneInputSchema).max(10),
  addresses: z.array(addressInputSchema).max(5),
});

export const householdSchema = z.object({
  name: zText(120),
  members: z
    .array(z.object({ personId: zId, role: z.enum(['adult', 'child']) }))
    .min(1, 'Agrega al menos una persona')
    .max(30),
  primaryContactPersonId: zOptId,
});

export const fieldDefinitionSchema = z.object({
  tab: zText(60),
  name: zText(80),
  dataType: z.enum(FIELD_TYPES),
  options: z.array(z.string().trim().min(1).max(80)).max(100).default([]),
  sortOrder: z.number().int().min(0).max(9999).default(0),
  isRequired: z.boolean().default(false),
});

export const fieldValuesSchema = z.object({
  values: z.array(z.object({ fieldId: zId, value: z.unknown() })).max(200),
});

export const noteCategorySchema = z.object({ name: zText(60), isRestricted: z.boolean().default(false) });
export const noteSchema = z.object({ categoryId: zOptId, body: zText(5000) });

// ---- Listas dinámicas ----
export const LIST_FIELDS = [
  'gender', 'membership', 'status', 'primary_campus', 'is_child', 'grade', 'age', 'birth_month',
  'marital_status', 'created_within_days', 'in_group', 'on_team', 'has_email', 'has_mobile',
  'checked_in_within_days', 'not_checked_in_within_days',
] as const;
export type ListField = (typeof LIST_FIELDS)[number];
export const LIST_OPS = ['eq', 'neq', 'gte', 'lte', 'in'] as const;

export const listConditionSchema = z.object({
  field: z.enum(LIST_FIELDS),
  op: z.enum(LIST_OPS),
  value: z.union([z.string().max(200), z.number(), z.boolean(), z.array(z.string().max(200)).max(50)]),
});

export const listSchema = z.object({
  name: zText(120),
  description: zOptText(500),
  campusId: zOptId,
  match: z.enum(['all', 'any']).default('all'),
  conditions: z.array(listConditionSchema).max(25).default([]),
});

// ---- Flujos de seguimiento ----
export const workflowSchema = z.object({
  name: zText(120),
  campusId: zOptId,
  steps: z.array(z.object({ name: zText(80), defaultAssigneePersonId: zOptId })).min(1).max(30),
});
export const workflowCardSchema = z.object({ personId: zId, assigneePersonId: zOptId });
export const workflowCardActionSchema = z.object({
  action: z.enum(['advance', 'back', 'complete', 'remove', 'reassign', 'snooze', 'comment']),
  assigneePersonId: zOptId,
  snoozeUntil: zOptDate,
  comment: zOptText(2000),
});

export const backgroundCheckSchema = z.object({
  status: z.enum(['pending', 'clear', 'flagged', 'expired']),
  completedOn: zOptDate,
  expiresOn: zOptDate,
  note: zOptText(1000),
});

// ---- Formularios ----
export const formFieldSchema = z.object({
  key: z.string().regex(/^[a-z0-9_]{1,40}$/, 'Clave inválida'),
  label: zText(120),
  type: z.enum(['text', 'textarea', 'email', 'phone', 'number', 'date', 'select', 'checkbox']),
  required: z.boolean().default(false),
  options: z.array(z.string().max(80)).max(50).default([]),
});
export const formSchema = z.object({
  name: zText(120),
  description: zOptText(2000),
  campusId: zOptId,
  isPublic: z.boolean().default(true),
  isActive: z.boolean().default(true),
  fields: z.array(formFieldSchema).min(1).max(50),
});
export const formSubmissionSchema = z.object({
  firstName: zText(80),
  lastName: zText(80),
  email: zEmail,
  answers: z.record(z.string(), z.unknown()),
});

export const mergePeopleSchema = z.object({ keepId: zId, mergeId: zId });

export const personSearchSchema = z.object({
  q: z.string().trim().max(100).optional(),
  campusId: zId.optional(),
  status: z.enum(PERSON_STATUSES).optional(),
  isChild: z.enum(['true', 'false']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
});

